import { ArrowBack } from "@mui/icons-material";
import { Alert, Button, Container, Grid, InputAdornment, TextField, Typography } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectUser } from "../../redux/slices/authSlice";
import { setEventData } from "../../redux/slices/createEventSlice";
import { fetchEvent, resetUploadReqStatus, selectEditEvent, selectEditEventFetchStatus, selectEditEventStatus, uploadEditEvent } from "../../redux/slices/editEventSlice";
import { fetchEvents } from "../../redux/slices/eventsSlice";
import { fetchPromoters, selectPromoters } from "../../redux/slices/promotersSlice";
import { AppDispatch } from "../../redux/store/store";
import { getGeoCodeFromAdress } from "../../util/firebaseUtil";
import { Ticket } from "../../util/types";
import { convertEventDataToLocal, isTicketPriceConfigurationValid, ticketRequiresFreeLimit } from "../../util/utils";
import LoadingView from "../general/LoadingView";
import InputFileUpload from "./InputFileUpload";
import ScrollableCheckboxList from "./ScrollableCheckBoxList";

export default function EditEventForm({ eventId, setSelectedEventId }: { eventId: string, setSelectedEventId: (t: string | null) => void }) {
    const dispatch: AppDispatch = useDispatch();
    const clubId = useSelector(selectUser)?.clubId;
    const reqStatusUpload = useSelector(selectEditEventStatus);
    const reqStatusFetch = useSelector(selectEditEventFetchStatus);
    const event = useSelector(selectEditEvent);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [imgUrl, setimgUrl] = useState<string | null>(null);
    const [validationErrorMsg, setValidationErrorMsg] = useState("");
    const [checked, setChecked] = useState<string[]>([]);
    const promoters = useSelector(selectPromoters);
    const [tickets, setTickets] = useState<Ticket[]>([]);

    const initialEventData = {
        ticketPrice: '',
        endTime: '',
        eventName: '',
        eventLocation: '',
        eventDescription: '',
        eventVenueName: '',
        startTime: '',
        age: '',
        genre: '',
        eventId: ''
    };
    const [localEventData, setLocalEventData] = useState(initialEventData);

    useEffect(() => {
        async function fetchData() {
            if (eventId) {
                //get selected event
                dispatch(fetchEvent({ eventId: eventId }))
                dispatch(fetchPromoters())
            }
        }
        fetchData();
    }, [eventId])

    useEffect(() => {
        async function fetchData() {
            if (event) {
                const eventDataLoaded = await convertEventDataToLocal(event);
                setimgUrl(event.eventImageCardUrl ?? event.eventImage ?? null);
                setLocalEventData(eventDataLoaded);
                // Populate tickets state from event
                setTickets(event.tickets && event.tickets.length > 0 ? event.tickets : []);
            }
        }
        fetchData();
    }, [event])

    useEffect(() => {
        if (promoters) {
            const filteredPromoters = promoters?.filter(promoter => promoter?.events?.includes(eventId ?? "")).map(x => x.uid) ?? [];
            setChecked(filteredPromoters);
        }
    }, [promoters])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setLocalEventData({ ...localEventData, [name]: value });
    };

    const handleDateTimeChange = (date: Dayjs | null, fieldName: string) => {
        // Convert the date to a string
        const dateString = date && date.isValid() ? date.toDate().toISOString() : '';

        // Update the state with the string date
        setLocalEventData({ ...localEventData, [fieldName]: dateString });
    };
    const handleAddTicket = () => {
        setTickets([
            ...tickets,
            {
                ticketTypeName: "",
                ticketTypePrice: 0,
                ticketsAvailable: 0,
                freeTicketsPerUserLimit: undefined,
                ticketTypeDescription: "",
                ticketBuyableUntil: "",
            },
        ]);
    };

    const handleTicketChange = (index: number, field: keyof Ticket, value: string) => {
        const updatedTickets = [...tickets];
        // Clone the ticket object to avoid mutating non-extensible/frozen objects
        const existing = updatedTickets[index] ? { ...updatedTickets[index] } : {} as Ticket;
        const newValue: any = (field === "ticketTypePrice" || field === "ticketsAvailable" || field === "freeTicketsPerUserLimit")
            ? (value === "" ? undefined : Number(value))
            : value;
        (existing as any)[field] = newValue;
        updatedTickets[index] = existing;
        setTickets(updatedTickets);
    };

    const handleTicketDateChange = (index: number, date: Dayjs | null) => {
        const updatedTickets = [...tickets];
        // Clone the ticket to avoid "object is not extensible" when event tickets are frozen
        const existing = updatedTickets[index] ? { ...updatedTickets[index] } : {} as Ticket;
        existing.ticketBuyableUntil = date && date.isValid() ? date.toDate().toISOString() : "";
        updatedTickets[index] = existing;
        setTickets(updatedTickets);
    };

    const handleRemoveTicket = (index: number) => {
        setTickets(tickets.filter((_, i) => i !== index));
    };



    async function handleSubmit() {
        setValidationErrorMsg("");
        if (await validateInputsAndShowError()) {
            const { lat, lng } = await getGeoCodeFromAdress(localEventData.eventLocation);
            if (lat && lng) {
                dispatch(uploadEditEvent({
                    eventData: {
                        ...event,
                        ...localEventData,
                        clubId: clubId ?? "",
                        eventLocation: { latitude: lat, longitude: lng },
                        eventAddress: localEventData.eventLocation,
                        eventImage: imgUrl ?? "",
                        tickets: tickets.map(t => ({ ...t, ticketBuyableUntil: t?.ticketBuyableUntil ? new Date(t.ticketBuyableUntil).toISOString() : t.ticketBuyableUntil })),
                        promoters: checked
                    },
                    file: selectedFile
                }));
                dispatch(setEventData(localEventData));
            } else {
                setValidationErrorMsg("Die angegebene Adresse ist falsch!")
            }
        }
    };

    async function validateInputsAndShowError() {
        const chain = localEventData.startTime &&
            localEventData.endTime &&
            localEventData.eventName &&
            localEventData.eventDescription &&
            localEventData.eventLocation &&
            (selectedFile || imgUrl) &&
            tickets.every(ticket =>
                ticket.ticketTypeName &&
                isTicketPriceConfigurationValid(ticket) &&
                (ticket.ticketsAvailable !== undefined && ticket.ticketsAvailable >= 0) &&
                (typeof ticket.ticketBuyableUntil === "string" && ticket.ticketBuyableUntil !== "")
            );
        if (!chain) {
            const errorList: string[] = [];
            if (!localEventData.startTime) {
                errorList.push("Startzeit");
            }
            if (!localEventData.endTime) {
                errorList.push("Endzeit");
            }
            if (!localEventData.eventName) {
                errorList.push("Event Name");
            }
            if (!localEventData.eventDescription) {
                errorList.push("Beschreibung");
            }
            if (!localEventData.eventLocation) {
                errorList.push("Event Adresse");
            }
            if (!selectedFile && !imgUrl) {
                errorList.push("Bilddatei");
            }
            if (tickets.some(ticket =>
                !ticket.ticketTypeName ||
                !isTicketPriceConfigurationValid(ticket) ||
                ticket.ticketsAvailable === undefined || ticket.ticketsAvailable === null || Number.isNaN(ticket.ticketsAvailable) ||
                ticket.ticketsAvailable < 0)) {
                errorList.push("Alle Ticketfelder müssen ausgefüllt sein. Kostenlose normale Tickets brauchen ein Limit von mindestens 1 pro User, bezahlte Tickets einen Preis größer als 0 (Menge: 0 oder größer).");
            }
            if (tickets.some(ticket => !ticket.ticketBuyableUntil || ticket.ticketBuyableUntil === "")) {
                errorList.push("Bitte geben Sie für jedes Ticket an, bis wann es gekauft werden kann (Verkauf bis).");
            }
            if (errorList.length > 0) {
                const errorMessage = `Die folgenden Felder sind nicht ausgefüllt, werden jedoch benötigt: ${errorList.join(', ')}`;
                setValidationErrorMsg(errorMessage);
                console.error(errorMessage);
            }
        }
        return chain;
    }

    if (reqStatusFetch === "pending" || reqStatusUpload === "pending") {
        return <LoadingView />
    }


    return (
        <Container maxWidth="md" sx={{ marginTop: 3, marginBottom: 10 }}>
            <Button
                variant="contained"
                startIcon={<ArrowBack />}
                sx={{ marginTop: 2, marginBottom: 4 }}
                onClick={() => {
                    //refetch all events (edited events too)
                    dispatch(fetchEvents({ clubId: clubId ?? "", forceRefresh: true }))
                    setSelectedEventId(null)
                    //remove alert
                    dispatch(resetUploadReqStatus())
                }}
            >
                Back
            </Button>
            {validationErrorMsg && <Alert severity="error">{validationErrorMsg}</Alert>}
            {reqStatusUpload === "succeeded" && <Alert severity="success">{"Event erfolgreich gespeichert!"}</Alert>}
            <Typography variant="h4" gutterBottom>
                Add Event
            </Typography>
            <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <InputFileUpload
                            selectedFile={selectedFile}
                            setSelectedFile={setSelectedFile}
                            url={event?.eventImageCardUrl ?? event?.eventImage}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <DateTimePicker
                            label="Start"
                            name="startTime"
                            ampm={false}
                            value={localEventData.startTime ? dayjs(localEventData.startTime) : null}
                            onChange={(date) => handleDateTimeChange(date, 'startTime')}
                        //renderInput={(props) => <TextField {...props} />}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <DateTimePicker
                            label="Ende"
                            name="endTime"
                            ampm={false}
                            value={localEventData.endTime ? dayjs(localEventData.endTime) : null}
                            onChange={(date) => handleDateTimeChange(date, 'endTime')}
                        //renderInput={(props) => <TextField {...props} />}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            required
                            label="Event Name"
                            name="eventName"
                            value={localEventData.eventName}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Mindestalter"
                            name="age"
                            value={localEventData.age}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Adresse"
                            name="eventLocation"
                            value={localEventData.eventLocation}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Name des Veranstaltungsortes"
                            name="eventVenueName"
                            value={localEventData.eventVenueName}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Genre"
                            name="genre"
                            value={localEventData.genre}
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
                            Caution: Editing Tickets ONLY for Guestlist right now!
                        </Typography>
                        <Typography variant="h6" gutterBottom>
                            Tickets
                        </Typography>
                    </Grid>


                    {tickets.map((ticket, index) => (
                        <Fragment key={index}>
                            <Grid item xs={3}>
                                <TextField
                                    label="Ticketname"
                                    value={ticket.ticketTypeName}
                                    onChange={(e) => handleTicketChange(index, "ticketTypeName", e.target.value)}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid item xs={2} >
                                <TextField
                                    label="Ticketpreis"
                                    type="number"
                                    value={ticket.ticketTypePrice}
                                    onChange={(e) => handleTicketChange(index, "ticketTypePrice", e.target.value)}
                                    fullWidth
                                    required
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">€</InputAdornment>,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <TextField
                                    label="Verfügbare Tickets"
                                    type="number"
                                    value={ticket.ticketsAvailable}
                                    onChange={(e) => handleTicketChange(index, "ticketsAvailable", e.target.value)}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <TextField
                                    label="Ticketbeschreibung"
                                    value={ticket.ticketTypeDescription}
                                    onChange={(e) => handleTicketChange(index, "ticketTypeDescription", e.target.value)}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <DateTimePicker
                                    label="Verkauf bis"
                                    ampm={false}
                                    value={ticket.ticketBuyableUntil ? dayjs(ticket.ticketBuyableUntil) : null}
                                    onChange={(date) => handleTicketDateChange(index, date)}
                                />
                            </Grid>
                            <Grid item xs={1} sx={{ display: "flex", alignItems: "flex-end" }}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={handleAddTicket}
                                    sx={{ marginRight: 1 }}
                                >
                                    +
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={() => handleRemoveTicket(index)}
                                >
                                    -
                                </Button>
                            </Grid>
                            {ticketRequiresFreeLimit(ticket) && (
                                <Grid item xs={12}>
                                    <TextField
                                        label="Kostenlose Tickets pro User"
                                        type="number"
                                        value={ticket.freeTicketsPerUserLimit ?? ""}
                                        onChange={(e) => handleTicketChange(index, "freeTicketsPerUserLimit", e.target.value)}
                                        fullWidth
                                        required
                                        inputProps={{ min: 1, step: 1 }}
                                        helperText="Pflichtfeld für normale kostenlose Tickets."
                                    />
                                </Grid>
                            )}
                        </Fragment>
                    ))}

                    {tickets.length < 1 && (
                        <Grid item xs={12}>
                            <Button variant="contained" color="primary" onClick={handleAddTicket} fullWidth>
                                + Neues Ticket Hinzufügen
                            </Button>
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Beschreibung"
                            name="eventDescription"
                            value={localEventData.eventDescription}
                            multiline
                            rows={4}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <ScrollableCheckboxList
                            clubPromoters={promoters ?? []}
                            setChecked={setChecked}
                            checked={checked} />
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            onClick={handleSubmit}
                        >
                            Save
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </Container>
    )
}
